import { DeviceDesktopIcon } from "@primer/octicons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { api, HttpError, qs, repoPath } from "../api/client";
import type { Evidence } from "../api/types";
import { gunzip, isShotPath, pairShots, untar, type TarFile } from "../lib/tar";
import { Card, CardContent, CardHeader } from "./ui/card";

/** Jobs whose artifacts may hold screenshots; others are not downloaded. */
const VISUAL_JOB = /e2e|screenshot|visual|shots|playwright/i;
const MAX_BYTES = 50 * 1024 * 1024;

function useShots(repo: string, e: Evidence) {
  return useQuery({
    queryKey: ["shots", repo, e.change_id, e.job, e.attempt],
    queryFn: async (): Promise<TarFile[]> => {
      const path = repoPath(repo, `/ci/artifacts${qs({ change: e.change_id, job: e.job, attempt: e.attempt })}`);
      try {
        const meta = await api.get<{ artifact: { size: number } }>(`${path}&meta=1`);
        if (meta.artifact.size > MAX_BYTES) return [];
        const r = await api.bytes(path);
        return untar(await gunzip(r.body), isShotPath);
      } catch (err) {
        if (err instanceof HttpError && err.status === 404) return [];
        throw err;
      }
    },
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Before/after screenshots a CI job uploaded as artifacts (paths under `screenshots/` or `shots/`),
 * for reviewing UI changes by looking at them. Renders nothing when no job uploaded any.
 */
export function Screenshots({ repo, run }: { repo: string; run: Evidence[] }) {
  const jobs = run.filter((e) => VISUAL_JOB.test(e.job) && (e.status === "pass" || e.status === "fail"));
  return (
    <>
      {jobs.map((e) => (
        <JobShots key={e.job} repo={repo} e={e} />
      ))}
    </>
  );
}

function JobShots({ repo, e }: { repo: string; e: Evidence }) {
  const q = useShots(repo, e);
  const pairs = useMemo(() => pairShots(q.data ?? []), [q.data]);
  if (!pairs.length) return null;
  return (
    <Card>
      <CardHeader>
        <DeviceDesktopIcon /> Visual changes <span className="text-xs font-normal text-muted-foreground">screenshots from {e.job}</span>
      </CardHeader>
      {pairs.map((p) => (
        <CardContent key={p.name} className="border-t first:border-t-0">
          <div className="mb-2 font-mono text-xs text-muted-foreground">{p.name}</div>
          <div className={p.before && p.after ? "grid grid-cols-2 gap-3 max-md:grid-cols-1" : ""}>
            {p.before && <Shot file={p.before} label="Before" />}
            {p.after && <Shot file={p.after} label={p.before ? "After" : undefined} />}
          </div>
        </CardContent>
      ))}
    </Card>
  );
}

function Shot({ file, label }: { file: TarFile; label?: string }) {
  const url = useMemo(() => URL.createObjectURL(new Blob([file.data as BlobPart], { type: mime(file.path) })), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return (
    <figure className="m-0">
      {label && <figcaption className="mb-1 text-xs font-semibold text-muted-foreground">{label}</figcaption>}
      <a href={url} target="_blank" rel="noreferrer">
        <img src={url} alt={`${label ?? "Screenshot"}: ${file.path}`} className="w-full rounded-md border" loading="lazy" />
      </a>
    </figure>
  );
}

const mime = (p: string) => (/\.png$/i.test(p) ? "image/png" : /\.webp$/i.test(p) ? "image/webp" : /\.gif$/i.test(p) ? "image/gif" : "image/jpeg");
